import { useCallback, useMemo } from 'react';

import { useEffectiveUser } from '../stores/authStore';
import { useOrgModules } from './useOrgModules';
import {
  isNavItemHiddenByExperience,
  isWidgetHiddenByExperience,
} from '../lib/profileUi';
import type {
  DashboardWidgetId,
  NavItemId,
  NavSection,
  ProfileExperience,
} from '../types/profiles';

type NavOrder = Partial<Record<NavSection, NavItemId[]>>;

const NO_HIDDEN: readonly NavItemId[] = [];
const NO_ORDER: NavOrder = {};

export interface ProfileUiState {
  /** profile.ui_config.experience ?? org experience ?? 'etabli' (plan §9.4). */
  experience: ProfileExperience;
  /** Sidebar items the profile hides (hiding is not forbidding). */
  hiddenNavItems: readonly NavItemId[];
  /** Per-section item order; unlisted items follow in their default order. */
  navOrder: NavOrder;
  /** Custom dashboard, or null for the default dashboard. */
  dashboardWidgets: { id: DashboardWidgetId; visible?: boolean }[] | null;
  /** Not hidden by the profile nor by the experience. Permissions/modules are checked by the caller. */
  isNavItemShown: (item: NavItemId) => boolean;
  isWidgetHiddenByExperience: (widget: DashboardWidgetId) => boolean;
}

/**
 * Effective UI configuration of the effective user (impersonated user when
 * impersonating). Reads `effective_profile.ui_config` only; permissions stay
 * in usePermissions.
 */
export function useProfileUi(): ProfileUiState {
  const user = useEffectiveUser();
  const { experience: orgExperience } = useOrgModules();
  const uiConfig = (user?.effective_profile ?? user?.profile)?.ui_config ?? null;

  const experience: ProfileExperience = uiConfig?.experience ?? orgExperience ?? 'etabli';
  const navigation = uiConfig?.navigation ?? null;
  const dashboardWidgets = uiConfig?.dashboard?.widgets ?? null;

  const hiddenNavItems = useMemo<readonly NavItemId[]>(
    () => navigation?.hidden ?? NO_HIDDEN,
    [navigation]
  );
  const navOrder = useMemo<NavOrder>(() => navigation?.order ?? NO_ORDER, [navigation]);

  const isNavItemShown = useCallback(
    (item: NavItemId) =>
      !hiddenNavItems.includes(item) && !isNavItemHiddenByExperience(item, experience),
    [hiddenNavItems, experience]
  );
  const widgetHidden = useCallback(
    (widget: DashboardWidgetId) => isWidgetHiddenByExperience(widget, experience),
    [experience]
  );

  return {
    experience,
    hiddenNavItems,
    navOrder,
    dashboardWidgets,
    isNavItemShown,
    isWidgetHiddenByExperience: widgetHidden,
  };
}
